/**
 * Download Utility Functions
 * Handles file downloads and data export functionality
 */

/**
 * Downloads data as a JSON file
 * @param data - The data object to download
 * @param filename - Name of the file (without extension)
 */
export const downloadJson = (data: any, filename: string = 'data'): void => {
    try {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the URL object
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading JSON file:', error);
        throw new Error('Failed to download data file');
    }
};

/**
 * Downloads data as a CSV file
 * @param data - Array of objects to convert to CSV
 * @param filename - Name of the file (without extension)
 */
export const downloadCsv = (data: any[], filename: string = 'data'): void => {
    try {
        if (!Array.isArray(data) || data.length === 0) {
            throw new Error('Data must be a non-empty array');
        }

        // Get headers from the first object
        const headers = Object.keys(data[0]);

        // Create CSV content
        const csvContent = [
            headers.join(','), // Header row
            ...data.map(row =>
                headers.map(header => {
                    const value = row[header];
                    // Handle values that might contain commas or quotes
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the URL object
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading CSV file:', error);
        throw new Error('Failed to download CSV file');
    }
};

/**
 * Downloads any blob as a file
 * @param blob - The blob to download
 * @param filename - Name of the file with extension
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
    try {
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the URL object
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading file:', error);
        throw new Error('Failed to download file');
    }
};

/**
 * Downloads data as a text file
 * @param text - The text content to download
 * @param filename - Name of the file (without extension)
 */
export const downloadText = (text: string, filename: string = 'data'): void => {
    try {
        const blob = new Blob([text], { type: 'text/plain' });
        downloadBlob(blob, `${filename}.txt`);
    } catch (error) {
        console.error('Error downloading text file:', error);
        throw new Error('Failed to download text file');
    }
};

/**
 * Formats file size in human readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.2 MB", "512 KB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Downloads a PDF blob
 * @param blob - The PDF blob to download
 * @param filename - Name of the file (with or without extension)
 */
export const downloadPDF = (blob: Blob, filename: string = 'document'): void => {
    try {
        // Ensure filename has .pdf extension
        const pdfFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
        downloadBlob(blob, pdfFilename);
    } catch (error) {
        console.error('Error downloading PDF file:', error);
        throw new Error('Failed to download PDF file');
    }
};

/**
 * Generates a filename with timestamp
 * @param baseName - Base name for the file
 * @param extension - File extension (without dot)
 * @returns Filename with timestamp
 */
export const generateTimestampedFilename = (baseName: string, extension: string): string => {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5); // Remove milliseconds and colons
    return `${baseName}_${timestamp}.${extension}`;
};